import { useEffect, useState } from "react";
import { ExternalLink } from "lucide-react";
import { toast } from "sonner";
import api, { apiErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Empty, Loader, Pagination, btn } from "@/components/Page";
import QuestPanel from "@/components/QuestPanel";
import { GoldCoin, FollisCoin } from "@/components/CoinIcons";
import { WheelOfFortunaModal } from "@/components/daily-ludus/WheelOfFortunaModal";
import JourneyPanel from "@/components/JourneyPanel";
import { LUDUS_IMAGE } from "@/lib/game";
import { OreRushEventNotice } from "@/components/OreRushEventNotice";

const BATTLES_PER_PAGE = 20;

const kindMeta = {
  ranked: { label: "Ranked", className: "border-amber-700/40 bg-amber-950/30 text-amber-300" },
  tournament: { label: "Tournament", className: "border-violet-700/40 bg-violet-950/30 text-violet-300" },
  exhibition: { label: "Scrimmage", className: "border-zinc-700/50 bg-zinc-900/70 text-zinc-300" },
};

function NewAccountFragmentBonus({ bonus }) {
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    if (!bonus?.active) return undefined;
    const timer = setInterval(() => setNow(Date.now()), 60000);
    return () => clearInterval(timer);
  }, [bonus?.active]);
  if (!bonus?.active || !bonus?.until) return null;
  const remaining = Math.max(0, new Date(bonus.until).getTime() - now);
  if (!remaining) return null;
  const hours = Math.ceil(remaining / 3600000);
  return <section data-testid="new-account-fragment-bonus" className="border-l-2 border-emerald-500 bg-emerald-950/20 px-4 py-3">
    <div className="flex flex-wrap items-center justify-between gap-3">
      <div>
        <div className="text-xs uppercase tracking-[0.18em] text-emerald-200">New Account Bonus</div>
        <p data-testid="new-account-fragment-bonus-copy" className="mt-1 text-sm text-zinc-100"><span className="font-semibold text-emerald-200">{bonus.mult}× fragment rewards</span> are active for your first {bonus.days} days in the arena.</p>
      </div>
      <span data-testid="new-account-fragment-bonus-countdown" className="text-xs tabular-nums text-emerald-200">{hours}h remaining</span>
    </div>
  </section>;
}

function formatDateTime(iso) {
  if (!iso) return "Unknown time";
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return "Unknown time";
  return new Intl.DateTimeFormat("en-US", {
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  }).format(date);
}

function LudusSummaryCard({ progression, arenaTotal }) {
  return (
    <div data-testid="daily-ludus-summary-card" className="relative overflow-hidden rounded-sm border border-white/10 bg-zinc-950/80 p-4 backdrop-blur-sm sm:p-5">
      <div className="text-[10px] uppercase tracking-[0.24em] text-zinc-500">Ludus summary</div>
      <div className="mt-4 grid gap-3 md:grid-cols-2 xl:grid-cols-3">
        <div data-testid="daily-ludus-metric-roster" className="rounded-sm border border-white/8 bg-black/15 px-4 py-3">
          <div className="text-[10px] uppercase tracking-[0.22em] text-zinc-500">Roster strength</div>
          <div className="mt-2 font-cinzel text-3xl text-zinc-50">{progression.active_roster}/{progression.roster_cap}</div>
          <div className="mt-1 text-sm text-zinc-400">Active gladiators ready for the house</div>
        </div>
        <div data-testid="daily-ludus-metric-battles" className="rounded-sm border border-white/8 bg-black/15 px-4 py-3">
          <div className="text-[10px] uppercase tracking-[0.22em] text-zinc-500">Total battles</div>
          <div className="mt-2 font-cinzel text-3xl text-zinc-50">{progression.total_battles.toLocaleString()}</div>
          <div className="mt-1 text-sm text-zinc-400">{arenaTotal.toLocaleString()} fought while away</div>
        </div>
        <div data-testid="daily-ludus-metric-treasury" className="rounded-sm border border-white/8 bg-black/15 px-4 py-3 md:col-span-2 xl:col-span-1">
          <div className="text-[10px] uppercase tracking-[0.22em] text-zinc-500">Treasury</div>
          <div className="mt-2 font-cinzel text-3xl text-zinc-50">{progression.gold.toLocaleString()} / {progression.follis.toLocaleString()}</div>
          <div className="mt-1 text-sm text-zinc-400">Gold and Follis on hand</div>
          <div data-testid="daily-ludus-metric-treasury-breakdown" className="mt-3 flex flex-wrap gap-3 text-xs text-zinc-400">
            <span className="inline-flex items-center gap-1"><GoldCoin size={12} /> {progression.gold.toLocaleString()} gold</span>
            <span className="inline-flex items-center gap-1"><FollisCoin size={12} /> {progression.follis.toLocaleString()} follis</span>
          </div>
        </div>
      </div>
    </div>
  );
}

function WheelEntryCard({ summary, onOpen }) {
  const status = summary?.status || {};
  const preview = (summary?.entries || []).slice(0, 4);
  return (
    <div data-testid="daily-ludus-wheel-entry" className="relative overflow-hidden rounded-sm border border-amber-500/40 bg-[radial-gradient(circle_at_top_left,rgba(251,191,36,0.16),transparent_28%),linear-gradient(135deg,rgba(120,53,15,0.28),rgba(24,24,27,0.96))] p-4">
      <div className="flex items-start justify-between gap-3">
        <div>
          <div className="text-[10px] uppercase tracking-[0.24em] text-amber-300">Wheel of Fortuna</div>
          <div className="mt-2 font-cinzel text-3xl text-zinc-50">{status.admin_test_mode ? "Admin test spins" : status.free_spin_ready ? "Free spin ready" : "Spin on cooldown"}</div>
          <div className="mt-1 text-sm text-zinc-300">{status.admin_test_mode ? `Unlimited test spins. Free-spin reset still lands ${formatDateTime(status.next_spin_at)}.` : status.free_spin_ready ? "Open the wheel and claim today’s server-timed reward spin." : `Next spin ${formatDateTime(status.next_spin_at)}`}</div>
        </div>
        <div className="rounded-full border border-amber-500/40 bg-black/20 p-3">
          <img src="/gold-coin.png" alt="Gold coin" className="h-5 w-5 object-contain drop-shadow-[0_0_10px_rgba(251,191,36,0.4)]" />
        </div>
      </div>
      <div data-testid="daily-ludus-wheel-preview" className="mt-4 flex flex-wrap gap-2 text-xs text-zinc-300">
        {preview.map((entry) => <span key={entry.id} className="rounded-sm border border-white/10 bg-black/20 px-2 py-1">{entry.label}</span>)}
      </div>
      <button data-testid="daily-ludus-wheel-open-button" type="button" onClick={onOpen} className={`${btn("primary")} mt-4 w-full`}>
        {status.can_spin ? "Open Wheel of Fortuna" : "View Wheel & Cooldown"}
      </button>
    </div>
  );
}

function DailyStreakCard({ streak, onClaim, busy }) {
  if (!streak) return null;
  const progressWidth = `${Math.max(0, Math.min(100, (Math.min(streak.current_streak, 5) / 5) * 100))}%`;
  const ladderSteps = [50, 100, 150, 200, 250];
  return (
    <section id="daily-streak-reward" data-testid="daily-streak-card" className="rounded-sm border border-amber-700/30 bg-[linear-gradient(135deg,rgba(120,53,15,0.28),rgba(24,24,27,0.96))] p-6">
      <div className="flex flex-col gap-5 lg:flex-row lg:items-center lg:justify-between">
        <div className="max-w-2xl">
          <div className="text-[11px] uppercase tracking-[0.24em] text-amber-300">Daily Login Streak Reward</div>
          <h2 data-testid="daily-streak-heading" className="mt-3 font-cinzel text-3xl text-zinc-50">Hold the streak. Claim bigger Follis every day.</h2>
          <p data-testid="daily-streak-copy" className="mt-2 max-w-xl text-sm text-zinc-300">
            Claim <span className="text-amber-300">+50 Follis</span> on day one, then climb in +50 steps each day until you reach the permanent cap of <span className="text-amber-300">+250 Follis</span>.
          </p>
          <div className="mt-4 grid gap-3 sm:grid-cols-3">
            <div data-testid="daily-streak-current-days" className="rounded-sm border border-white/10 bg-black/10 px-3 py-3">
              <div className="text-[10px] uppercase tracking-[0.22em] text-zinc-500">Current streak</div>
              <div className="mt-1 font-cinzel text-2xl text-zinc-50">{streak.current_streak} day{streak.current_streak === 1 ? "" : "s"}</div>
            </div>
            <div data-testid="daily-streak-reward-value" className="rounded-sm border border-white/10 bg-black/10 px-3 py-3">
              <div className="text-[10px] uppercase tracking-[0.22em] text-zinc-500">Today’s reward</div>
              <div className="mt-1 flex items-center gap-2 font-cinzel text-2xl text-amber-300"><FollisCoin size={18} /> {streak.claimable ? streak.reward_follis : streak.current_reward_follis}</div>
            </div>
            <div data-testid="daily-streak-cap-value" className="rounded-sm border border-white/10 bg-black/10 px-3 py-3">
              <div className="text-[10px] uppercase tracking-[0.22em] text-zinc-500">Daily cap</div>
              <div className="mt-1 flex items-center gap-2 font-cinzel text-2xl text-zinc-50"><FollisCoin size={18} /> {streak.max_daily_follis}</div>
            </div>
          </div>
        </div>

        <div className="w-full max-w-md rounded-sm border border-white/10 bg-black/20 p-5">
          <div className="flex items-center justify-between gap-3">
            <div>
              <div className="text-[10px] uppercase tracking-[0.22em] text-zinc-500">Five-day ladder</div>
              <div className="mt-1 font-cinzel text-lg text-zinc-50">{streak.claimable ? `Claim +${streak.reward_follis} Follis now` : "Reward already claimed today"}</div>
            </div>
            <div className="rounded-sm border border-amber-700/40 bg-amber-950/40 px-3 py-1 text-xs uppercase tracking-[0.22em] text-amber-300">
              Day {Math.max(1, Math.min(streak.claimed_today ? streak.current_streak : streak.streak_after_claim, 5))}
            </div>
          </div>
          <div data-testid="daily-streak-progress-track" className="mt-4 h-2 overflow-hidden rounded-full bg-zinc-900 ring-1 ring-white/5">
            <div data-testid="daily-streak-progress-bar" className="h-full rounded-full bg-gradient-to-r from-amber-500 via-orange-400 to-amber-200 transition-[width] duration-700" style={{ width: progressWidth }} />
          </div>
          <div className="mt-3 flex justify-between text-[11px] uppercase tracking-[0.22em] text-zinc-500">
            {ladderSteps.map((amt) => <span key={amt}>{amt}</span>)}
          </div>
          <div data-testid="daily-streak-status" className="mt-4 text-sm text-zinc-300">
            {streak.claimed_today ? "You already claimed today’s streak reward. Return tomorrow to keep the chain alive." : streak.reset ? "A missed day broke the old streak. Claim now to start a new one." : "Claim today’s reward to push the streak forward."}
          </div>
          <div data-testid="daily-streak-tomorrow-reward" className="mt-2 text-xs uppercase tracking-[0.22em] text-zinc-500">
            Tomorrow’s reward: <span className="text-amber-300">+{streak.tomorrow_reward_follis} follis</span>
          </div>
          <button
            data-testid="claim-streak-button"
            onClick={onClaim}
            disabled={!streak.claimable || busy}
            className={`${btn("primary")} mt-5 w-full ${!streak.claimable ? "opacity-60" : ""}`}
          >
            {busy ? "Claiming..." : streak.claimable ? `Claim +${streak.reward_follis} Follis` : "Claimed Today"}
          </button>
        </div>
      </div>
    </section>
  );
}

function SandsEaseBanner({ event }) {
  const [liveEvent, setLiveEvent] = useState(event);
  useEffect(() => {
    setLiveEvent(event);
    let mounted = true;
    const refresh = () => api.get("/sands-ease-event").then((response) => {
      if (mounted) setLiveEvent(response.data);
    }).catch(() => {});
    window.addEventListener("focus", refresh);
    const timer = setInterval(refresh, 30000);
    return () => { mounted = false; window.removeEventListener("focus", refresh); clearInterval(timer); };
  }, [event]);
  if (!liveEvent?.active) return null;
  return <section data-testid="daily-ludus-sands-ease-banner" className="border border-cyan-500/50 bg-cyan-950/20 px-5 py-4">
    <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
      <div>
        <div className="text-[10px] uppercase tracking-[0.24em] text-cyan-200">Sands Ease Event</div>
        <p data-testid="daily-ludus-sands-ease-copy" className="mt-1 text-sm text-zinc-200">Sands Run opponents are {liveEvent.reduction_percent}% weaker. Legacy Sands rewards are {liveEvent.legacy_reward_multiplier}×, including armor finds. Expedition Kit reductions still stack with this event.</p>
      </div>
      <div className="flex flex-wrap gap-2">
        <a data-testid="daily-ludus-sands-ease-run-link" href="/events?tab=adventure" className={`${btn("ghost")} whitespace-nowrap`}>Enter Sands Run</a>
        <a data-testid="daily-ludus-sands-ease-legacy-link" href="/events?tab=legacy-sands" className={`${btn("ghost")} whitespace-nowrap`}>Enter Legacy Sands</a>
      </div>
    </div>
  </section>;
}

export default function DailyLudus() {
  const { refreshUser, user } = useAuth();
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [battlePage, setBattlePage] = useState(1);
  const [stickySince, setStickySince] = useState(null);
  const [wheelOpen, setWheelOpen] = useState(false);

  const load = async (preserveSince = false, page = 1) => {
    try {
      const since = preserveSince ? stickySince : null;
      const response = await api.get("/daily-ludus", { params: { ...(since ? { since } : {}), battle_page: page } });
      setData(response.data);
      setStickySince((prev) => prev || response.data.while_away_since);
    } catch {
      setData(false);
    }
  };

  useEffect(() => {
    load(false, 1);
    document.title = "Your Ludus | Lanista — Gladiator Manager";
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const battles = data?.arena?.battles || [];
  const pagedBattles = battles;
  const battlePages = Math.max(1, Math.ceil((data?.arena?.total || 0) / BATTLES_PER_PAGE));

  const changeBattlePage = async (page) => {
    if (page === battlePage) return;
    setBattlePage(page);
    await load(true, page);
  };

  const claimDailyStreak = async () => {
    setBusy(true);
    try {
      const response = await api.post("/daily-ludus/streak/claim");
      toast.success(`Daily streak claimed — +${response.data.reward_follis} Follis`);
      await refreshUser?.();
      window.dispatchEvent(new Event("lanista:refresh-badges"));
      await load(true, battlePage);
    } catch (error) {
      toast.error(apiErr(error));
    } finally {
      setBusy(false);
    }
  };

  if (data === false) return <Empty label="The Daily Ludus could not be loaded right now." />;
  if (!data) return <Loader label="Preparing your Daily Ludus…" />;

  const progression = data.progression;
  const streak = data.daily_streak;
  const arenaTotal = data.arena?.total || 0;
  const hasAwayWindow = !!data.has_away_window;
  const advances = data.gladiator_status?.advances || [];
  const advancingGladiatorCount = new Set(advances.map((advance) => advance.gladiator_id).filter(Boolean)).size;
  const advancesCount = advances.length;

  return (
    <div data-testid="daily-ludus-page" className="space-y-8">
      <section data-testid="daily-ludus-header" className="relative isolate min-h-[25rem] overflow-hidden border-y border-amber-700/40">
        <img data-testid="daily-ludus-training-yard-image" src={LUDUS_IMAGE} alt="Gladiators training in the Ludus yard" className="absolute inset-0 h-full w-full object-cover object-center" />
        <div aria-hidden="true" className="absolute inset-0 bg-gradient-to-r from-black/95 via-black/65 to-black/30" />
        <div className="relative flex min-h-[25rem] max-w-4xl flex-col justify-end px-6 py-8 sm:px-10 sm:py-12">
          <div className="text-[11px] uppercase tracking-[0.3em] text-amber-300">Training yard · {progression.active_roster} active</div>
          <h1 data-testid="daily-ludus-greeting" className="mt-3 font-cinzel text-4xl text-zinc-50 sm:text-5xl lg:text-6xl">{data.lanista_name}&rsquo;s Ludus</h1>
          <p data-testid="daily-ludus-subtitle" className="mt-4 max-w-2xl text-sm text-zinc-200 sm:text-base">The stable is in motion. Review each fighter&rsquo;s standing, arena readiness, and weekly work before sending them to the sands.</p>
          <div className="mt-8 flex flex-wrap gap-x-8 gap-y-3 border-t border-white/25 pt-4 text-xs uppercase tracking-[0.18em] text-zinc-300">
            <span data-testid="daily-ludus-header-roster">{progression.active_roster}/{progression.roster_cap} active</span>
            <span data-testid="daily-ludus-header-streak">{streak.current_streak} day streak</span>
            <span data-testid="daily-ludus-header-reward">Next reward {streak.claimable ? streak.reward_follis : streak.current_reward_follis} Follis</span>
          </div>
        </div>
      </section>

      <NewAccountFragmentBonus bonus={user?.new_account_fragment_bonus} />
      <OreRushEventNotice testId="daily-ludus-ore-rush-banner" />
      <SandsEaseBanner event={data.sands_ease_event} />

      <section data-testid="daily-ludus-progression-grid" className="grid gap-4 xl:grid-cols-[1.45fr_0.95fr] xl:items-stretch">
        <LudusSummaryCard progression={progression} arenaTotal={arenaTotal} />
        <WheelEntryCard summary={data.wheel_of_fortuna} onOpen={() => setWheelOpen(true)} />
      </section>

      <JourneyPanel />

      <DailyStreakCard streak={streak} onClaim={claimDailyStreak} busy={busy} />

      <section data-testid="daily-ludus-training-status" className="rounded-sm border border-emerald-800/20 bg-zinc-950/70 p-6">
        <div className="flex flex-col gap-4 md:flex-row md:items-start md:justify-between">
          <div>
            <div className="text-[11px] uppercase tracking-[0.24em] text-emerald-300">Gladiator Status</div>
            <h2 className="mt-2 font-cinzel text-3xl text-zinc-50">Training continued while you were away.</h2>
            <p data-testid="daily-ludus-training-summary-copy" className="mt-2 text-sm text-zinc-400">
              {advancesCount > 0 ? (
                <>
                  <span className="text-zinc-100">{advancingGladiatorCount}</span> gladiator{advancingGladiatorCount === 1 ? "" : "s"} advanced stats to the next skill level across <span className="text-zinc-100">{advancesCount}</span> advance{advancesCount === 1 ? "" : "s"}.
                </>
              ) : (
                <>No gladiators advanced stats to the next skill level while you were away.</>
              )}
            </p>
          </div>
          <div className="flex flex-wrap gap-3">
            <a data-testid="training-summary-link-button" href="/training?tab=summary" className={btn("primary")}>Open Training Summary</a>
            <a data-testid="training-advances-link-button" href="/training?tab=advances" className={btn("ghost")}>Open Stat Advances</a>
          </div>
        </div>

        <div className="mt-6 space-y-3">
          {advances.length === 0 ? (
            <Empty label="No new stat title advances since your last activity — but you can still open the training summary to review recent gains." />
          ) : (
            advances.map((advance) => (
              <div key={advance.id} data-testid={`daily-ludus-advance-${advance.id}`} className="flex flex-col gap-3 rounded-sm border border-white/8 bg-black/10 p-4 lg:flex-row lg:items-center lg:justify-between">
                <div>
                  <div className="text-[10px] uppercase tracking-[0.22em] text-zinc-500">{advance.stat_key}</div>
                  <div className="mt-1 text-sm text-zinc-100">
                    <a data-testid={`daily-ludus-advance-gladiator-${advance.id}`} href={`/gladiator/${advance.gladiator_id}`} className="font-semibold text-amber-300 hover:text-amber-200">
                      {advance.gladiator_name}
                    </a>{" "}
                    advanced from <span className="text-zinc-300">{advance.old_title}</span> to <span className="text-emerald-300">{advance.new_title}</span>.
                  </div>
                </div>
                <div className="text-xs text-zinc-500">{formatDateTime(advance.created_at)}</div>
              </div>
            ))
          )}
        </div>
      </section>

      <section id="daily-ludus-arena" data-testid="daily-ludus-arena-section" className="rounded-sm border border-red-900/20 bg-zinc-950/70 p-6">
        <div className="flex flex-col gap-3 md:flex-row md:items-end md:justify-between">
          <div>
            <div className="text-[11px] uppercase tracking-[0.24em] text-red-300">Arena</div>
            <h2 className="mt-2 font-cinzel text-3xl text-zinc-50">Battles fought while away</h2>
            <p className="mt-2 text-sm text-zinc-400">This feed shows battles recorded after you left the game and links to the logged-in play-by-play result.</p>
          </div>
          <div className="flex flex-wrap gap-3 text-sm text-zinc-400">
            <span data-testid="daily-ludus-arena-count">{arenaTotal} total</span>
            <span data-testid="daily-ludus-arena-scar-count" className="text-red-300">{data.arena.scarred_count} scar report{data.arena.scarred_count === 1 ? "" : "s"}</span>
          </div>
        </div>

        {arenaTotal === 0 ? (
          <div className="mt-5"><Empty label={hasAwayWindow ? "No arena battles were recorded while you were away." : "No away period has been recorded yet. Leave the game, then return here to review what happened in your absence."} /></div>
        ) : (
          <div className="mt-6 space-y-3">
            {pagedBattles.map((battle) => {
              const meta = kindMeta[battle.kind] || kindMeta.ranked;
              return (
                <div key={battle.id} data-testid={`battle-row-${battle.id}`} className="rounded-sm border border-white/8 bg-black/10 p-4">
                  <div className="flex flex-col gap-3 xl:flex-row xl:items-center xl:justify-between">
                    <div className="min-w-0 space-y-2">
                      <div className="flex flex-wrap items-center gap-2 text-[10px] uppercase tracking-[0.22em] text-zinc-500">
                        <span className={`rounded-sm border px-2 py-1 ${meta.className}`}>{meta.label}</span>
                        <span>{formatDateTime(battle.created_at)}</span>
                        {battle.scarred ? <span className="rounded-sm border border-red-700/40 bg-red-950/30 px-2 py-1 text-red-300">Scarred</span> : null}
                      </div>
                      <div className="flex flex-wrap items-center gap-1 text-sm text-zinc-100">
                        <a data-testid={`battle-gladiator-link-${battle.id}`} href={battle.gladiator_link || battle.battle_link} target="_blank" rel="noreferrer" className="font-semibold text-amber-300 hover:text-amber-200">{battle.gladiator_name}</a>
                        <span className="text-zinc-500">vs</span>
                        <a data-testid={`battle-opponent-link-${battle.id}`} href={battle.opponent_gladiator_link || battle.battle_link} target="_blank" rel="noreferrer" className="font-semibold text-zinc-100 hover:text-amber-200">{battle.opponent_name}</a>
                        <span className="text-zinc-500">·</span>
                        <a data-testid={`battle-opponent-lanista-link-${battle.id}`} href={battle.opponent_lanista_link || "/profile"} target="_blank" rel="noreferrer" className="text-zinc-400 hover:text-amber-200">{battle.opponent_lanista_name || "Unknown lanista"}</a>
                      </div>
                      {battle.scarred && battle.scar_note ? <div data-testid={`battle-scar-note-${battle.id}`} className="text-xs text-red-300">Scar report: {battle.scar_note}</div> : null}
                    </div>
                    <a data-testid={`battle-link-${battle.id}`} href={battle.battle_link} target="_blank" rel="noreferrer" className={`${btn("ghost")} whitespace-nowrap`}>
                      View Play-by-Play <ExternalLink size={14} className="inline-block ml-2" />
                    </a>
                  </div>
                </div>
              );
            })}
            <Pagination page={battlePage} totalPages={battlePages} onChange={changeBattlePage} testid="daily-ludus-battle-pagination" />
          </div>
        )}
      </section>

      <section data-testid="daily-ludus-objectives-section" className="space-y-4">
        <div>
          <div className="text-[11px] uppercase tracking-[0.24em] text-amber-300">Daily / Weekly Objectives</div>
          <h2 className="mt-2 font-cinzel text-3xl text-zinc-50">Arena objectives</h2>
          <p className="mt-2 text-sm text-zinc-400">This is the same objective board from the Arena page.</p>
        </div>
        <QuestPanel onClaimed={() => window.dispatchEvent(new Event("lanista:refresh-badges"))} />
      </section>

      <WheelOfFortunaModal
        open={wheelOpen}
        onClose={() => setWheelOpen(false)}
        summary={data.wheel_of_fortuna}
        onRewardClaimed={async () => {
          await refreshUser?.();
          window.dispatchEvent(new Event("lanista:refresh-badges"));
          await load(true, battlePage);
        }}
      />
    </div>
  );
}